import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, HeartPulse, Wallet, ShieldCheck } from 'lucide-react';

const cases = [
  {
    industry: 'Global Payments',
    title: 'Major Payment Processor',
    description: 'A top-tier global payment processor handling billions in transactions struggled with fragmented monitoring and slow incident response.',
    result: 'Reduced incident response time by 40% and prevented an estimated $2.5M in annual outage costs.',
    metrics: [
      { label: 'Response Time', value: '-40%' },
      { label: 'Cost Savings', value: '$2.5M' },
    ],
    tags: ['Digital Transformation', 'QA Strategy'],
    accent: 'from-blue-500 to-cyan-500',
    accentColor: '#00d4ff',
    icon: Briefcase,
    slug: 'global-payments',
  },
  {
    industry: 'Healthcare',
    title: 'Medical Software Platform',
    description: 'Modernising legacy patient-care software to enable rapid, safe feature delivery without compromising compliance.',
    result: 'Regression testing reduced from 2 weeks to 1 day, with zero critical defects post-launch.',
    metrics: [
      { label: 'Regression Time', value: '-90%' },
      { label: 'Critical Bugs', value: '0' },
    ],
    tags: ['Legacy Modernisation', 'Test Automation'],
    accent: 'from-emerald-500 to-teal-500',
    accentColor: '#10b981',
    icon: HeartPulse,
    slug: 'healthcare-compliance',
  },
  {
    industry: 'Fintech',
    title: 'High-Growth Fintech',
    description: 'A rapid-growth fintech unicorn faced slowing release cycles due to flaky tests and accumulating technical debt.',
    result: 'Accelerated from bi-weekly to daily releases while reducing customer-reported bugs by 30%.',
    metrics: [
      { label: 'Release Rate', value: '10x' },
      { label: 'Bug Reduction', value: '30%' },
    ],
    tags: ['AI Implementation', 'CI/CD'],
    accent: 'from-violet-500 to-fuchsia-500',
    accentColor: '#a855f7',
    icon: Wallet,
    slug: 'fintech-growth',
  },
];

const CaseStudies = () => {
  return (
    <section id="stories" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 circuit-pattern opacity-10" />
      <div className="absolute inset-0"
        style={{ background: 'radial-gradient(circle at 30% 50%, rgba(0,212,255,0.04), transparent 50%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center space-x-2 rounded-full px-4 py-1.5 mb-6"
            style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)' }}>
            <ShieldCheck className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs font-medium text-cyan-400 tracking-wide uppercase">Client Results</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
            Built for <span className="gradient-text">critical industries</span>
          </h2>
          <p className="text-gray-400 leading-relaxed">
            Representative outcomes from 15 years across Payments, Healthcare, and FinTech — the environments where quality failures have real consequences.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cases.map((c, i) => {
            const Icon = c.icon;
            return (
              <Link
                to={`/case-study/${c.slug}`}
                key={i}
                className="group relative rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 block overflow-hidden"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = `${c.accentColor}30`}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
              >
                {/* Top gradient bar */}
                <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${c.accent}`} />

                {/* Glow */}
                <div className="absolute top-0 right-0 w-48 h-48 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `radial-gradient(circle, ${c.accentColor}10 0%, transparent 70%)` }} />

                <div className="relative z-10">
                  {/* Icon + industry */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${c.accent} p-2 flex-shrink-0`}>
                      <Icon className="w-full h-full text-white" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{c.industry}</span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">{c.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5">{c.description}</p>

                  {/* Result */}
                  <p className="text-sm font-medium text-white mb-4 pb-4 border-b border-white/5"
                    style={{ borderLeft: `3px solid ${c.accentColor}`, paddingLeft: '12px' }}>
                    {c.result}
                  </p>

                  {/* Metrics */}
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    {c.metrics.map((m, j) => (
                      <div key={j} className="rounded-lg p-3 text-center"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div className="text-xl font-bold" style={{ color: c.accentColor }}>{m.value}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{m.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {c.tags.map((tag, t) => (
                      <span key={t} className="px-2.5 py-0.5 text-xs rounded-full text-gray-400"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
